'use client';

import {
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState
} from 'react';

const COOKIE_NAME = 'active_font';

function setFontCookie(font: string) {
  if (typeof window === 'undefined') return;
  document.cookie = `${COOKIE_NAME}=${encodeURIComponent(font)}; path=/; max-age=31536000; SameSite=Lax; ${window.location.protocol === 'https:' ? 'Secure;' : ''}`;
}

type FontContextType = {
  activeFont: string;
  activeFontArabic: string;
  setActiveFont: (font: string) => void;
  setActiveFontArabic: (font: string) => void;
};

const FontContext = createContext<FontContextType | undefined>(undefined);

export function FontProvider({
  children,
  initialFont,
  initialFontArabic
}: {
  children: ReactNode;
  initialFont?: string;
  initialFontArabic?: string;
}) {
  const [activeFont, setActiveFontState] = useState(initialFont || '');
  const [activeFontArabic, setActiveFontArabicState] = useState(
    initialFontArabic || ''
  );

  const setActiveFont = useCallback((font: string) => {
    const value = font === '__default__' ? '' : font;
    setActiveFontState(value);
    setFontCookie(value);
  }, []);

  const setActiveFontArabic = useCallback((font: string) => {
    const value = font === '__default__' ? '' : font;
    setActiveFontArabicState(value);
    if (typeof window === 'undefined') return;
    document.cookie = `active_font_arabic=${encodeURIComponent(value)}; path=/; max-age=31536000; SameSite=Lax; ${window.location.protocol === 'https:' ? 'Secure;' : ''}`;
  }, []);

  useEffect(() => {
    const html = document.documentElement;
    if (activeFont) {
      html.style.setProperty(
        '--font-sans-override',
        `${activeFont}, ui-sans-serif, system-ui, sans-serif`
      );
    } else {
      html.style.removeProperty('--font-sans-override');
    }
  }, [activeFont]);

  useEffect(() => {
    const html = document.documentElement;
    if (activeFontArabic) {
      html.style.setProperty(
        '--font-arabic-override',
        `${activeFontArabic}, ui-sans-serif, sans-serif`
      );
    } else {
      html.style.removeProperty('--font-arabic-override');
    }
  }, [activeFontArabic]);

  return (
    <FontContext.Provider
      value={{
        activeFont,
        activeFontArabic,
        setActiveFont,
        setActiveFontArabic
      }}
    >
      {children}
    </FontContext.Provider>
  );
}

export function useFontConfig() {
  const context = useContext(FontContext);
  if (context === undefined) {
    throw new Error('useFontConfig must be used within a FontProvider');
  }
  return context;
}
